import React, { useState, useEffect, lazy, Suspense } from 'react';
import dayjs from 'dayjs';

import { useTheme } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import useMediaQuery from '@mui/material/useMediaQuery';

import useStyles from '../styles';
import ReportsIcon from '../styles/icons/ReportsIcon';
import Header from '../components/Header';
import DateRangePicker from '../components/DateRangePicker';
import SensorDataPicker from '../Widgets/Compare/SensorDataPicker';
import SensorDataPlotWrapper from '../Widgets/Compare/SensorDataPlotWrapper';
import MetricDataPlotWrapper from '../Widgets/Compare/MetricDataPlotWrapper';
import { getSelectedEndDate, getSelectedStartDate } from '../state/selectors';
import { useAppSelector } from '../state/store';
import { MetricOptions } from '../Widgets/Compare/helpers';

const SensorList = lazy(() => import('../Widgets/Compare/SensorList'));

function Compare(): JSX.Element {
  const { classes } = useStyles();
  const theme = useTheme();

  const startDate = useAppSelector(getSelectedStartDate);
  const endDate = useAppSelector(getSelectedEndDate);

  const [showSensorsList, setShowSensorsList] = useState(false);
  const [showFloatingBtns, setShowFloatingBtns] = useState(false);
  const [metricSelection, setMetricSelection] = useState<MetricOptions>();

  const isDesktopView = useMediaQuery(theme.breakpoints.up('md'));
  const isRawDataAllowed = dayjs(endDate).diff(startDate, 'month') < 2; // disable if timeRange is more than 2 months

  useEffect(() => {
    if (!metricSelection) {
      if (isRawDataAllowed) setMetricSelection(MetricOptions.Raw);
      else setMetricSelection(MetricOptions.Daily);
    } else if (!isRawDataAllowed && metricSelection === MetricOptions.Raw)
      setMetricSelection(MetricOptions.Daily);
  }, [startDate, endDate, metricSelection, isRawDataAllowed]);

  useEffect(() => {
    if (isDesktopView) setShowSensorsList(true);
  }, [isDesktopView]);

  const handleScroll = (event: React.UIEvent | React.TouchEvent) => {
    const target = event.target as HTMLElement;
    const { scrollHeight, scrollTop, clientHeight } = target;
    const scrollPos = scrollHeight - scrollTop - clientHeight;
    const topRect = scrollHeight - clientHeight;
    if (isDesktopView) {
      if (scrollPos !== topRect) setShowFloatingBtns(true);
      else setShowFloatingBtns(false);
    }
  };

  // for mobile screens scroll seems ineffective and compare from screen height instead
  const controlDirection = () => {
    if (!isDesktopView) {
      if (window.pageYOffset > window.innerHeight / 4) setShowFloatingBtns(true);
      else setShowFloatingBtns(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', controlDirection);
    return () => {
      window.removeEventListener('scroll', controlDirection);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classes.bodyContent} style={{ overflowY: 'hidden' }}>
      <Header />
      <div className={`${classes.dashboard} ${classes.reportContainer}`}>
        {showSensorsList && (
          <div
            onScroll={handleScroll}
            onTouchMove={handleScroll}
            className={`${classes.dashboardTile} ${classes.dashboardTileScrollable}`}
            style={{ minWidth: isDesktopView ? '400px' : '300px', flex: '1 1 auto' }}
          >
            <Suspense
              fallback={<Skeleton variant="rectangular" className={classes.compareListPanel} />}
            >
              <SensorList
                showFloatingBtns={showFloatingBtns}
                hideList={() => setShowSensorsList(false)}
              />
            </Suspense>
          </div>
        )}
        <div
          className={`${classes.dashboardTile} ${classes.dashboardTileScrollable} ${classes.marginLeftNoSm} ${classes.compareRightContainer}`}
          style={{
            display: `${!isDesktopView && showSensorsList ? 'none' : 'block'}`,
            width: '100%',
          }}
        >
          <DateRangePicker
            isRawDataAllowed={isRawDataAllowed}
            metricSelection={metricSelection}
            setMetricSelection={setMetricSelection}
          />
          <SensorDataPicker />
          {metricSelection && (
            <div style={{ position: 'relative' }}>
              {metricSelection === MetricOptions.Raw && isRawDataAllowed ? (
                <SensorDataPlotWrapper />
              ) : (
                <MetricDataPlotWrapper metricSelection={metricSelection} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Button to show/hide sensors list */}
      <Tooltip title={`${showSensorsList ? 'Hide Sensors list' : 'Show Sensors List'}`}>
        <Fab
          size="medium"
          className={classes.floatingBtn}
          style={{
            textTransform: 'inherit',
            zIndex: 100,
            left: `${isDesktopView && !showSensorsList ? '75px' : 'unset'}`,
            display: `${isDesktopView && !showSensorsList ? 'block' : ''}`,
          }}
          onClick={() => setShowSensorsList(!showSensorsList)}
        >
          {showSensorsList ? <ReportsIcon /> : <FormatListBulletedIcon />}
        </Fab>
      </Tooltip>
    </div>
  );
}
export default Compare;
